import { cn } from '@lib/utils/helpers'
import { FilterToolbarSearch } from './FilterToolbarSearch'
import { ListFiltersButton } from './ListFiltersButton'
import type { ListSearchToolbarProps } from './ListSearchToolbar.types'

/**
 * Invitee-style search + filter toolbar shared by list pages.
 * Stacks on small screens; row layout from `md` up.
 */
export function ListSearchToolbar({
  searchValue,
  searchPlaceholder,
  onSearchChange,
  onOpenFilters,
  activeFilterCount = 0,
  actions,
  middle,
  actionsOverlay,
  testIdPrefix = 'list',
  searchLabel = 'Search',
  className,
  hideSearch = false,
}: ListSearchToolbarProps) {
  const hasMiddle = Boolean(middle)

  return (
    <div
      data-testid={`${testIdPrefix}-toolbar`}
      className={cn(
        hasMiddle
          ? 'flex w-full min-w-0 flex-col gap-3 xl:flex-row xl:items-center xl:gap-4'
          : 'flex w-full min-w-0 flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between',
        // No search means one child, so `justify-between` would pin it left.
        hideSearch && 'md:justify-end xl:justify-end',
        className,
      )}
    >
      {hideSearch ? null : (
      <FilterToolbarSearch
        data-testid={`${testIdPrefix}-search-wrap`}
        inputDataTestId={`${testIdPrefix}-search-input`}
        value={searchValue}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        label={searchLabel}
        wrapClassName={cn(
          'min-w-0 w-full max-w-none md:max-w-lg',
          hasMiddle && '!w-full !max-w-none md:!max-w-lg xl:shrink-0',
        )}
        inputClassName="focus:border-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]/15"
      />
      )}

      {hasMiddle ? (
        <div
          data-testid={`${testIdPrefix}-toolbar-middle`}
          className="flex min-w-0 w-full flex-1 flex-wrap items-center justify-start gap-2 sm:justify-center xl:justify-center"
        >
          {middle}
        </div>
      ) : null}

      <div
        data-testid={`${testIdPrefix}-toolbar-actions`}
        className={cn(
          'relative flex w-full min-w-0 flex-wrap items-center justify-stretch gap-2 sm:justify-end sm:gap-3 md:w-auto md:shrink-0',
          hasMiddle && 'xl:w-auto xl:shrink-0',
        )}
      >
        {actions}
        <ListFiltersButton
          onClick={onOpenFilters}
          activeFilterCount={activeFilterCount}
          testIdPrefix={testIdPrefix}
        />
        {actionsOverlay}
      </div>
    </div>
  )
}
